import { useEffect, useRef } from "react";

// Lightweight animated network topology on <canvas>. Brand-blue nodes and links
// with subtle mouse parallax. Pauses for prefers-reduced-motion.
export default function NetworkCanvas() {
    const canvasRef = useRef(null);
    const mouse = useRef({ x: 0, y: 0, active: false });

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        const reduce = window.matchMedia(
            "(prefers-reduced-motion: reduce)",
        ).matches;

        let width = 0;
        let height = 0;
        let dpr = Math.min(window.devicePixelRatio || 1, 2);
        let nodes = [];
        let raf;

        const NODE_COUNT = () => (width < 520 ? 26 : 42);

        const resize = () => {
            const rect = canvas.getBoundingClientRect();
            width = rect.width;
            height = rect.height;
            canvas.width = width * dpr;
            canvas.height = height * dpr;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            build();
        };

        const build = () => {
            const count = NODE_COUNT();
            nodes = Array.from({ length: count }, () => ({
                x: Math.random() * width,
                y: Math.random() * height,
                vx: (Math.random() - 0.5) * 0.35,
                vy: (Math.random() - 0.5) * 0.35,
                r: Math.random() * 1.8 + 1.2,
            }));
        };

        const draw = () => {
            ctx.clearRect(0, 0, width, height);
            const maxDist = width < 520 ? 110 : 150;

            for (let i = 0; i < nodes.length; i++) {
                const n = nodes[i];
                if (!reduce) {
                    n.x += n.vx;
                    n.y += n.vy;
                }
                if (n.x < 0 || n.x > width) n.vx *= -1;
                if (n.y < 0 || n.y > height) n.vy *= -1;

                // mouse parallax pull
                if (mouse.current.active) {
                    const dx = mouse.current.x - n.x;
                    const dy = mouse.current.y - n.y;
                    const d = Math.hypot(dx, dy);
                    if (d < 140) {
                        n.x -= (dx / d) * 0.6;
                        n.y -= (dy / d) * 0.6;
                    }
                }
            }

            // links
            for (let i = 0; i < nodes.length; i++) {
                for (let j = i + 1; j < nodes.length; j++) {
                    const a = nodes[i];
                    const b = nodes[j];
                    const dist = Math.hypot(a.x - b.x, a.y - b.y);
                    if (dist < maxDist) {
                        const alpha = (1 - dist / maxDist) * 0.5;
                        ctx.strokeStyle = `rgba(56, 189, 248, ${alpha})`;
                        ctx.lineWidth = 1;
                        ctx.beginPath();
                        ctx.moveTo(a.x, a.y);
                        ctx.lineTo(b.x, b.y);
                        ctx.stroke();
                    }
                }
            }

            // nodes
            for (const n of nodes) {
                const grd = ctx.createRadialGradient(
                    n.x,
                    n.y,
                    0,
                    n.x,
                    n.y,
                    n.r * 4,
                );
                grd.addColorStop(0, "rgba(125, 211, 252, 0.9)");
                grd.addColorStop(1, "rgba(2, 132, 199, 0)");
                ctx.fillStyle = grd;
                ctx.beginPath();
                ctx.arc(n.x, n.y, n.r * 4, 0, Math.PI * 2);
                ctx.fill();

                ctx.fillStyle = "rgba(224, 242, 254, 0.95)";
                ctx.beginPath();
                ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
                ctx.fill();
            }

            raf = requestAnimationFrame(draw);
        };

        const onMove = (e) => {
            const rect = canvas.getBoundingClientRect();
            mouse.current = {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top,
                active: true,
            };
        };
        const onLeave = () => (mouse.current.active = false);

        resize();
        raf = requestAnimationFrame(draw);
        window.addEventListener("resize", resize);
        canvas.addEventListener("mousemove", onMove);
        canvas.addEventListener("mouseleave", onLeave);

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", resize);
            canvas.removeEventListener("mousemove", onMove);
            canvas.removeEventListener("mouseleave", onLeave);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            data-testid="hero-visual-canvas"
            aria-hidden="true"
            className="absolute inset-0 h-full w-full"
        />
    );
}
